import { useForm } from "@mantine/form";
import { TextInput, Text, Paper, PaperProps, Button, Anchor, Stack, Alert } from "@mantine/core";
import { useMutation } from "react-query";
import { forgotPassword } from "./Authentication";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";

export function ForgotPasswordForm(props: PaperProps) {
    const form = useForm({
        initialValues: {
            email: "",
        },
        validate: {
            email: (val) => (/^\S+@\S+$/.test(val) ? null : "Invalid email"),
        },
    });

    const mutation = useMutation({
        mutationFn: (values: { email: string }) => {
            return forgotPassword(values.email);
        },
    });

    const { isLoading, isSuccess, error } = mutation;

    let errorMessage = "";
    if (error) {
        errorMessage = (error as any)?.response?.data?.message || "Something went wrong, please try again";
    }

    if (isSuccess) {
        return (
            <Paper radius="md" withBorder className="w-[30rem] p-12 max-w-[100vw] mt-[25%]" {...props}>
                <Stack align="center" gap="lg">
                    <IconCheck size={48} color="#2f9e44" />
                    <h1 className="text-center">Check your email</h1>
                    <Text ta="center" c="dimmed">
                        If an account with that email exists, we've sent a link to reset your password. Check your inbox
                        and follow the instructions.
                    </Text>
                    <Anchor href="/login" size="sm">
                        Back to sign in
                    </Anchor>
                </Stack>
            </Paper>
        );
    }

    return (
        <Paper radius="md" withBorder className="w-[30rem] p-12 max-w-[100vw] mt-[25%]" {...props}>
            <h1 className="mb-4 text-3xl">Forgot password</h1>
            <Text c="dimmed" size="sm" mb="xl" ta="left">
                Enter your email address and we'll send you a link to reset your password.
            </Text>

            {errorMessage && <Alert icon={<IconAlertCircle size="1rem" />} title={errorMessage} color="red" mb="md" />}

            <form
                onSubmit={form.onSubmit((values) => {
                    mutation.mutate(values);
                })}
            >
                <Stack>
                    <TextInput
                        required
                        withAsterisk={false}
                        label="Email"
                        placeholder="your@email.com"
                        value={form.values.email}
                        onChange={(event) => form.setFieldValue("email", event.currentTarget.value)}
                        error={form.errors.email && "Invalid email"}
                        radius="md"
                    />
                    <Button type="submit" loading={isLoading} fullWidth mt="xl" size="lg">
                        Send reset link
                    </Button>
                    <Text ta="center" mt="md">
                        <Anchor href="/login" size="sm">
                            Back to sign in
                        </Anchor>
                    </Text>
                </Stack>
            </form>
        </Paper>
    );
}
